'use client';

import React from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { Flame, ShieldCheck, Truck, Gift } from 'lucide-react';

export default function AboutSection() {
  return (
    <section id="about" className="pt-14 md:pt-20 pb-0 bg-[#0F382A] relative overflow-hidden border-b border-[#0A261D]">
      {/* Subtle atmospheric ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none overflow-hidden">
        <div className="absolute -top-24 left-1/4 w-80 h-80 rounded-full bg-[#E09F55]/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 -right-20 w-80 h-80 rounded-full bg-[#144030]/60 blur-2xl pointer-events-none" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Top Header: Title & Subtitle */}
        <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
          <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl text-white tracking-tight leading-[1.18]">
            Why Shop with Elavarasi Foods?
          </h2>
          <p className="text-xs sm:text-sm text-[#DDDAC4]/90 font-sans mt-3 leading-relaxed">
            From our wood-fired cauldrons to your doorstep — we&apos;re here to make authentic South Indian snack shopping pure, fresh, and unforgettable.
          </p>
        </div>

        {/* 3-Column Layout: Left Features | Center Large Mascot at Base | Right Features */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-4 items-end">
          
          {/* Left Column: 2 Features */}
          <div className="lg:col-span-3 xl:col-span-3 flex flex-col space-y-8 sm:space-y-12 pb-6 lg:pb-14 order-2 lg:order-1">
            
            {/* Feature 1: Free and Fast Delivery */}
            <div className="space-y-2.5 group">
              <div className="w-10 h-10 rounded-full bg-white/10 border border-[#E09F55]/30 text-[#F2BC6F] flex items-center justify-center shadow-xs backdrop-blur-xs group-hover:bg-[#E09F55] group-hover:text-[#0F382A] group-hover:border-[#E09F55] transition-all duration-300">
                <Truck className="w-5 h-5 stroke-[2]" />
              </div>
              <h3 className="font-display font-semibold text-base sm:text-lg text-white group-hover:text-[#F2BC6F] transition-colors">
                Free and Fast Dispatch
              </h3>
              <p className="text-xs sm:text-sm text-[#DDDAC4]/80 font-sans leading-relaxed">
                Authentic snacks fried fresh daily and dispatched fast — free delivery across India on orders over ₹499.
              </p>
            </div>

            {/* Feature 2: 100% Pure Heritage Quality */}
            <div className="space-y-2.5 group">
              <div className="w-10 h-10 rounded-full bg-white/10 border border-[#E09F55]/30 text-[#F2BC6F] flex items-center justify-center shadow-xs backdrop-blur-xs group-hover:bg-[#E09F55] group-hover:text-[#0F382A] group-hover:border-[#E09F55] transition-all duration-300">
                <Flame className="w-5 h-5 stroke-[2]" />
              </div>
              <h3 className="font-display font-semibold text-base sm:text-lg text-white group-hover:text-[#F2BC6F] transition-colors">
                Wood-Fired Batch Frying
              </h3>
              <p className="text-xs sm:text-sm text-[#DDDAC4]/80 font-sans leading-relaxed">
                Hand-twisted Kai Murukku and golden crisps slow-fried in pure cold-pressed coconut oil and pure cow ghee.
              </p>
            </div>

          </div>

          {/* Center Column: elaravari toon Mascot Image Anchored to Base with Increased Size */}
          <div className="lg:col-span-6 xl:col-span-6 flex justify-center items-end self-end order-1 lg:order-2">
            <div className="relative w-full max-w-[420px] sm:max-w-[500px] lg:max-w-[580px] xl:max-w-[640px] flex justify-center items-end">
              {/* Warm glow behind mascot */}
              <div className="absolute bottom-6 w-72 h-72 sm:w-96 sm:h-96 rounded-full bg-[#E09F55]/25 blur-3xl pointer-events-none -z-10" />
              
              <div className="relative w-full h-[320px] sm:h-[400px] md:h-[460px] lg:h-[500px] xl:h-[540px] flex items-end justify-center">
                <Image
                  src="/images/elaravari-toon.png"
                  alt="Elavarasi Foods Mascot"
                  fill
                  sizes="(max-width: 768px) 100vw, 640px"
                  priority
                  unoptimized
                  className="object-contain object-bottom drop-shadow-[0_20px_35px_rgba(0,0,0,0.35)] hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
            </div>
          </div>

          {/* Right Column: 2 Features */}
          <div className="lg:col-span-3 xl:col-span-3 flex flex-col space-y-8 sm:space-y-12 pb-6 lg:pb-14 order-3">
            
            {/* Feature 3: Guaranteed Freshness */}
            <div className="space-y-2.5 group">
              <div className="w-10 h-10 rounded-full bg-white/10 border border-[#E09F55]/30 text-[#F2BC6F] flex items-center justify-center shadow-xs backdrop-blur-xs group-hover:bg-[#E09F55] group-hover:text-[#0F382A] group-hover:border-[#E09F55] transition-all duration-300">
                <ShieldCheck className="w-5 h-5 stroke-[2]" />
              </div>
              <h3 className="font-display font-semibold text-base sm:text-lg text-white group-hover:text-[#F2BC6F] transition-colors">
                Guaranteed Freshness
              </h3>
              <p className="text-xs sm:text-sm text-[#DDDAC4]/80 font-sans leading-relaxed">
                Nitrogen-sealed 3-layer pouches lock in crispness for 90 days. Zero preservatives, zero palm oil.
              </p>
            </div>

            {/* Feature 4: Custom Gifts & Bulk Orders */}
            <div className="space-y-2.5 group">
              <div className="w-10 h-10 rounded-full bg-white/10 border border-[#E09F55]/30 text-[#F2BC6F] flex items-center justify-center shadow-xs backdrop-blur-xs group-hover:bg-[#E09F55] group-hover:text-[#0F382A] group-hover:border-[#E09F55] transition-all duration-300">
                <Gift className="w-5 h-5 stroke-[2]" />
              </div>
              <h3 className="font-display font-semibold text-base sm:text-lg text-white group-hover:text-[#F2BC6F] transition-colors">
                Custom Gifts & Bulk Orders
              </h3>
              <p className="text-xs sm:text-sm text-[#DDDAC4]/80 font-sans leading-relaxed">
                Personalized metallic gold foil-embossed return gift boxes for weddings, corporate celebrations, and events.
              </p>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
}

